import logger from "./logger";
import { WS_BASE, getWsTicket } from "./api";
import { authService } from "./auth";
import { logWsDiagnostic } from "./blankPageDiagnostics";

/**
 * WorkspaceSocketManager
 *
 * Provides a single, shared, multiplexed WebSocket connection to the unified
 * workspace socket endpoint (`/api/workspace/ws/workspace`).
 *
 * Eliminates duplicate connections and duplicate ticket requests across
 * FinanceNotificationContext, IncomingCallContext, and other top-level providers.
 */
class WorkspaceSocketManager {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.statusListeners = new Set();
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.isConnecting = false;
    this.connectingPromise = null;
    this.idleTimer = null;
    this.isAuthError = false;
  }

  isConnected() {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  subscribeStatus(listener) {
    if (typeof listener !== "function") return () => {};
    this.statusListeners.add(listener);
    // Send immediate current status
    try {
      listener(this.isConnected());
    } catch (err) {
      logger.error("[WorkspaceSocket] status listener error:", err);
    }
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  notifyStatus(connected) {
    this.statusListeners.forEach((listener) => {
      try {
        listener(connected);
      } catch (err) {
        logger.error("[WorkspaceSocket] notifyStatus error:", err);
      }
    });
  }

  subscribe(listener) {
    if (typeof listener !== "function") return () => {};

    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    this.listeners.add(listener);

    // If not connected or connecting, initiate connection
    if (!this.isConnected() && !this.isConnecting) {
      this.connect();
    }

    return () => {
      this.listeners.delete(listener);
      // If no more listeners remain, close connection after a short grace period
      if (this.listeners.size === 0) {
        this.scheduleIdleClose();
      }
    };
  }

  scheduleIdleClose() {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (this.listeners.size === 0) {
        this.close();
      }
    }, 5000);
  }

  send(payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const message = typeof payload === "string" ? payload : JSON.stringify(payload);
        this.ws.send(message);
        return true;
      } catch (err) {
        logger.error("[WorkspaceSocket] Error sending message:", err);
        return false;
      }
    }
    return false;
  }

  async connect() {
    if (this.isConnecting && this.connectingPromise) {
      return this.connectingPromise;
    }
    if (this.isConnected()) {
      return this.ws;
    }

    const token = authService.getToken();
    if (!token) {
      return null;
    }

    this.isConnecting = true;
    this.isAuthError = false;

    this.connectingPromise = (async () => {
      try {
        logWsDiagnostic("workspace_shared_socket", "connecting");
        const ticket = await getWsTicket();
        if (!ticket) {
          this.isConnecting = false;
          return null;
        }

        const wsUrl = `${WS_BASE}/api/workspace/ws/workspace?ticket=${encodeURIComponent(ticket)}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          logWsDiagnostic("workspace_shared_socket", "open");
          this.reconnectAttempts = 0;
          this.isConnecting = false;
          this.notifyStatus(true);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.listeners.forEach((listener) => {
              try {
                listener(data);
              } catch (err) {
                logger.error("[WorkspaceSocket] Listener error:", err);
              }
            });
          } catch (parseErr) {
            // Ignore malformed ping/frames
          }
        };

        ws.onclose = (event) => {
          logWsDiagnostic("workspace_shared_socket", "close", {
            code: event.code,
            reason: event.reason,
          });
          this.ws = null;
          this.isConnecting = false;
          this.notifyStatus(false);

          // Definitive auth error codes
          if (
            (event.code === 1008 || event.code === 4001 || event.code === 4401) &&
            !authService.hasRefreshToken()
          ) {
            this.isAuthError = true;
            return;
          }

          // Automatically reconnect if there are still active listeners
          if (!this.isAuthError && this.listeners.size > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(3000 * this.reconnectAttempts, 15000);
            logWsDiagnostic("workspace_shared_socket", "reconnect_scheduled", {
              attempt: this.reconnectAttempts,
              delay,
            });
            if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
            this.reconnectTimer = setTimeout(() => {
              if (this.listeners.size > 0) {
                this.connect();
              }
            }, delay);
          }
        };

        ws.onerror = (error) => {
          logWsDiagnostic("workspace_shared_socket", "error");
          logger.warn("[WorkspaceSocket] Socket error encountered");
        };

        this.ws = ws;
        return ws;
      } catch (err) {
        this.isConnecting = false;
        logger.error("[WorkspaceSocket] Error initiating shared socket:", err?.message || err);
        return null;
      } finally {
        this.connectingPromise = null;
      }
    })();

    return this.connectingPromise;
  }

  close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.onclose = null; // suppress reconnect on explicit close
        this.ws.close();
      } catch (err) {
        /* ignore */
      }
      this.ws = null;
    }
    this.isConnecting = false;
    this.connectingPromise = null;
    this.notifyStatus(false);
  }
}

export const workspaceSocket = new WorkspaceSocketManager();
export default workspaceSocket;
